/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  Amounts,
  HttpStatusCode,
  PaytoType,
  Paytos,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import { useAccountDetails } from "../../hooks/account.js";
import { IntAmounts } from "../regional/CreateCashout.js";
import { Props, State } from "./index.js";

export function useComponentState({
  account,
  tab,
  routeChargeWallet,
  routeCreateWireTransfer,
  routePublicAccounts,
  routeOperationDetails,
  routeWireTransfer,
  routeCashout,
  onOperationCreated,
  onClose,
  routeClose,
}: Props): State {
  const result = useAccountDetails(account);

  if (!result) {
    return {
      status: "loading",
      error: undefined,
    };
  }

  if (result instanceof TalerError) {
    return {
      status: "loading-error",
      error: result,
    };
  }

  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
        return {
          status: "login",

          reason: "forbidden",
        };
      case HttpStatusCode.NotFound:
        return {
          status: "login",

          reason: "not-found",
        };
      default: {
        assertUnreachable(result);
      }
    }
  }

  const { body: data } = result;

  const balance = Amounts.parseOrThrow(data.balance.amount);

  const debitThreshold = Amounts.parseOrThrow(data.debit_threshold);
  const payto = Paytos.fromString(data.payto_uri);

  if (
    payto.type === "fail" ||
    !payto.body.targetType ||
    (payto.body.targetType !== PaytoType.IBAN && payto.body.targetType !== PaytoType.TalerBank)
  ) {
    return {
      status: "invalid-iban",
      error: data,
    };
  }

  const balanceIsDebit = data.balance.credit_debit_indicator == "debit";
  const limit = IntAmounts.toIntAmount(balance, balanceIsDebit)
    .increment(debitThreshold).result;

  const positiveBalance = balanceIsDebit
    ? Amounts.zeroOfAmount(balance)
    : balance;

  return {
    status: "ready",
    onOperationCreated,
    error: undefined,
    tab,
    routeCashout,
    routeOperationDetails,
    routeCreateWireTransfer,
    routePublicAccounts,

    onClose,
    routeClose,
    routeChargeWallet,
    routeWireTransfer,
    account,
    limit,
    balance: positiveBalance,
  };
}
